import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { Study } from '../../../../../../models/study';
import { Module } from '../../../../../../models/module';
import { ModuleItemSubType } from '../../../../../../models/module-item-sub-type';
import { ModuleItem } from '../../../../../../models/module-item';
import { Language, LanguageCode } from '../../../../../../models/language';
import {
  MODULE_ITEM_DESCRIPTION_IDENTIFIER,
  MODULE_ITEM_LABEL_IDENTIFIER,
  MODULE_ITEM_QUESTION_IDENTIFIER,
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
  RANDOMIZATION_VALUES,
} from '../../../../../../core/dashboard-constants';
import { StudyType } from '../../../../../../models/study-type';
import { AppState } from '../../../../../../state';
import { ModuleItemDialogComponent } from '../../module-item-dialog.component';
import { PrivilegeService } from '../../../../../services/privilege/privilege.service';
import { selectSelectedLanguage, selectSupportedLanguages } from '../../../../../state/study/study.selectors';
import { isThereEmptyTranslation } from '../../../../helpers/translation-helper';
import { createModuleItem, updateModuleItem } from '../../../../../state/module-item/module-item.actions';
import { ModuleItemCoreComponent } from '../module-item-core/module-item-core.component';
import { PreviewService } from '../../../../../services/preview/preview.service';
import { StudyTranslation } from '../../../../../../models/study-translation';
import { OrbitItem } from '../../../../../../models/orbit-item';
import { ORBIT_ITEM_ICONS } from '../../../../../../models/orbit-item-icons';
import { ModuleItemAvailableAttribute } from '../../../../../../models/module-item-attribute';
import { RoutingService } from '../../../../../services/routing/routing.service';

@Component({
  selector: 'dashboard-orbits',
  templateUrl: './orbits.component.html',
  styleUrls: ['./orbits.component.scss'],
})
export class OrbitsComponent implements OnInit, OnDestroy {
  @ViewChild(ModuleItemCoreComponent, { static: true })
  dashboardModuleItemCore: ModuleItemCoreComponent;

  @Input() dialogData;

  orbitsForm: FormGroup;

  selectedStudy: Study;
  selectedModule: Module;
  moduleItemSubType: ModuleItemSubType;
  selectedModuleItem: ModuleItem;
  isDuplicate: boolean;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  isEdit: boolean;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  StudyType = StudyType;
  ORBIT_ITEM_ICONS = ORBIT_ITEM_ICONS;

  formSubmitted = false;

  orbitItems: OrbitItem[] = [];
  currentMatIcon = ORBIT_ITEM_ICONS[0].matIcon;
  isEditingOrbitItem = false;
  editingModuleItemIndex: number;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    private moduleItemDialogRef: MatDialogRef<ModuleItemDialogComponent>,
    private previewService: PreviewService,
    private changeDetectorRef: ChangeDetectorRef,
    public privilegeService: PrivilegeService,
    private routingService: RoutingService
  ) {}

  ngOnInit(): void {
    this.orbitsForm = this.formBuilder.group({
      moduleItemCore: [],
      translations: this.formBuilder.array([]),
      numberOrbits: [null, [Validators.required, Validators.min(1), Validators.max(10)]],
      orbitItemName: [''],
      orbitItemIcon: [ORBIT_ITEM_ICONS[0].matIcon],
      reuseOrbitItems: [false],
      mainOrbitItem: [null],
    });

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );
    this.subscriptions.add(
      this.store
        .select(selectSelectedLanguage)
        .subscribe((selectedLanguage) => (this.selectedLanguage = selectedLanguage))
    );

    this.selectedStudy = this.dialogData?.selectedStudy;
    this.selectedModule = this.dialogData?.selectedModule;
    this.moduleItemSubType = this.dialogData?.moduleItemSubType;
    this.selectedModuleItem = this.dialogData?.moduleItem;
    this.isDuplicate = this.dialogData?.isDuplicate;

    this.isEdit = !!this.selectedModuleItem && !this.isDuplicate;

    this.translations.clear();
    if (this.isEdit || this.isDuplicate) {
      if (this.selectedModuleItem?.numberOrbits) {
        this.numberOrbits.setValue(this.selectedModuleItem.numberOrbits);
        this.previewService.setPreviewData({
          numberOrbits: this.selectedModuleItem.numberOrbits,
        });
      }

      if (this.selectedModuleItem?.reuseOrbitItems) {
        this.reuseOrbitItems.setValue(this.selectedModuleItem.reuseOrbitItems);
      }

      if (this.selectedModuleItem?.orbitItems?.length) {
        this.orbitItems = [...this.selectedModuleItem.orbitItems];
        this.previewService.setPreviewData({
          orbitItems: this.selectedModuleItem.orbitItems,
        });
        this.orbitItems.every((orbitItem) => {
          if (orbitItem.mainItem) {
            this.mainOrbitItem.setValue(orbitItem.name);
            return false;
          } else {
            return true;
          }
        });
      }
    }

    this.subscriptions.add(
      this.numberOrbits.valueChanges.subscribe((numberOrbitsValueChange) =>
        this.previewService.setPreviewData({
          numberOrbits: numberOrbitsValueChange,
        })
      )
    );
  }

  get moduleItemCore(): AbstractControl {
    return this.orbitsForm.get('moduleItemCore');
  }

  get translations(): FormArray {
    return this.orbitsForm.get('translations') as FormArray;
  }

  get numberOrbits(): AbstractControl {
    return this.orbitsForm.get('numberOrbits');
  }

  get orbitItemName(): AbstractControl {
    return this.orbitsForm.get('orbitItemName');
  }

  get orbitItemIcon(): AbstractControl {
    return this.orbitsForm.get('orbitItemIcon');
  }

  get reuseOrbitItems(): AbstractControl {
    return this.orbitsForm.get('reuseOrbitItems');
  }

  get mainOrbitItem(): AbstractControl {
    return this.orbitsForm.get('mainOrbitItem');
  }

  getOrbitItemMatIcon(orbitItem: OrbitItem): string {
    return this.ORBIT_ITEM_ICONS.find(
      (orbitItemIcon) => orbitItemIcon.icon === orbitItem.icon && orbitItemIcon.iconCode === orbitItem.iconCode
    ).matIcon;
  }

  emitOrbitItems(): void {
    this.previewService.setPreviewData({
      orbitItems: this.orbitItems,
    });
  }

  resetOrbitItem(): void {
    this.orbitItemName.setValue('');
    this.orbitItemIcon.setValue(ORBIT_ITEM_ICONS[0].matIcon);
    this.currentMatIcon = ORBIT_ITEM_ICONS[0].matIcon;
  }

  isValidOrbitItem(): boolean {
    if (!this.orbitItemName.value.trim().length) {
      this.toastrService.error('Item name is required.');
      return false;
    }

    let nameAlreadyExists = false;
    this.orbitItems.every((orbitItem) => {
      if (orbitItem.name === this.orbitItemName.value.trim()) {
        this.toastrService.error('Item name already exists.');
        nameAlreadyExists = true;
        return false;
      } else {
        return true;
      }
    });

    if (nameAlreadyExists) {
      return false;
    }

    if (!this.orbitItemIcon.value) {
      this.toastrService.error('Item icon is required.');
      return false;
    }

    return true;
  }

  addOrbitItem(): void {
    if (!this.isValidOrbitItem()) {
      return;
    }

    const orbitIcon = ORBIT_ITEM_ICONS.find(
      (currentOrbitItem) => currentOrbitItem.matIcon === this.orbitItemIcon.value
    );

    const orbitItem = {
      name: this.orbitItemName.value.trim(),
      icon: orbitIcon.icon,
      iconCode: orbitIcon.iconCode,
      mainItem: false,
    };

    this.orbitItems.push(orbitItem);

    this.resetOrbitItem();
    this.emitOrbitItems();
  }

  editOrbitItem(orbitItem: OrbitItem, index: number): void {
    this.isEditingOrbitItem = true;
    this.editingModuleItemIndex = index;
    this.orbitItemName.setValue(orbitItem.name);
    const matIcon = this.getOrbitItemMatIcon(orbitItem);
    this.orbitItemIcon.setValue(matIcon);
    this.currentMatIcon = matIcon;
  }

  cancelEditingOrbitItem(): void {
    this.isEditingOrbitItem = false;
    this.editingModuleItemIndex = null;
    this.resetOrbitItem();
  }

  saveOrbitItem(): void {
    if (!this.isValidOrbitItem()) {
      return;
    }

    const orbitIcon = ORBIT_ITEM_ICONS.find(
      (currentOrbitItem) => currentOrbitItem.matIcon === this.orbitItemIcon.value
    );

    this.orbitItems[this.editingModuleItemIndex] = {
      ...this.orbitItems[this.editingModuleItemIndex],
      name: this.orbitItemName.value,
      icon: orbitIcon.icon,
      iconCode: orbitIcon.iconCode,
    };

    this.isEditingOrbitItem = false;
    this.editingModuleItemIndex = null;
    this.resetOrbitItem();
    this.emitOrbitItems();
  }

  removeOrbitItem(index: number): void {
    if (this.orbitItems[index].name === this.mainOrbitItem.value) {
      this.mainOrbitItem.setValue(null);
    }
    this.orbitItems.splice(index, 1);
    this.emitOrbitItems();
  }

  buildTranslationsForRequest(translations: StudyTranslation[]) {
    const translationsForRequest = [];

    let labelTranslation = null;

    translations?.forEach((translation) => {
      if (translation.identifier === MODULE_ITEM_LABEL_IDENTIFIER) {
        labelTranslation = translation;
        translationsForRequest.push({
          ...translation,
          translation: this.moduleItemCore.value.label,
        });
      } else {
        translationsForRequest.push(translation);
      }
    });

    if (!labelTranslation) {
      translationsForRequest.push({
        id: 0,
        language: LanguageCode.ENGLISH,
        identifier: MODULE_ITEM_LABEL_IDENTIFIER,
        translation: this.moduleItemCore.value.label,
      });
    }

    const translationIdentifiersOrder = [
      MODULE_ITEM_QUESTION_IDENTIFIER,
      MODULE_ITEM_DESCRIPTION_IDENTIFIER,
      MODULE_ITEM_LABEL_IDENTIFIER,
    ];

    translationsForRequest.sort(
      (translation1, translation2) =>
        translationIdentifiersOrder.indexOf(translation1.identifier) -
        translationIdentifiersOrder.indexOf(translation2.identifier)
    );

    return translationsForRequest;
  }

  setMainOrbitItem(): void {
    this.orbitItems.forEach((orbitItem, i) => {
      this.orbitItems[i] = {
        ...orbitItem,
        mainItem: orbitItem.name === this.mainOrbitItem.value,
      };
    });
  }

  saveModuleItem(): void {
    this.formSubmitted = true;

    const allTranslations = [...this.moduleItemCore.value.translations, ...this.translations.value];

    if (isThereEmptyTranslation(this.moduleItemCore.value.translations, MODULE_ITEM_QUESTION_IDENTIFIER)) {
      this.toastrService.error('Please populate Question for all supported languages');
      return;
    }

    if (!this.orbitItems.length) {
      this.toastrService.error('You need to add at least one orbit item');
      return;
    }

    if (this.mainOrbitItem?.value === null) {
      this.toastrService.error('You need to choose main orbit item');
      return;
    }

    if (this.orbitsForm.invalid) {
      return;
    }

    const translationsForRequest = this.buildTranslationsForRequest(allTranslations);

    this.setMainOrbitItem();

    let moduleItem = {
      ...this.orbitsForm.value,
      subType: this.moduleItemSubType?.type || this.selectedModuleItem?.subType,
      type: this.moduleItemSubType?.itemType || this.selectedModuleItem?.type,
      moduleId: this.selectedModule?.id,
      isEditing: this.isEdit,
      translations: translationsForRequest,
      orbitItems: this.orbitItems,
      attributes: [
        {
          attribute: ModuleItemAvailableAttribute.ORBITS,
          moduleItemId: this.selectedModuleItem?.id || 0,
        },
      ],
      routing: this.routingService.isRoutingDataChanged()
        ? this.routingService.getRoutingData()
        : this.selectedModuleItem?.routing,
    };

    if (this.selectedModule?.randomization === RANDOMIZATION_VALUES.CATEGORY) {
      if (!this.moduleItemCore.value.categoryId) {
        this.toastrService.error('Please select Category Label');
        return;
      }

      moduleItem = {
        ...moduleItem,
        categoryId: this.moduleItemCore.value.categoryId,
      };
    }

    delete moduleItem.moduleItemCore;
    delete moduleItem.label;
    delete moduleItem.orbitItemName;
    delete moduleItem.orbitItemIcon;
    delete moduleItem.mainOrbitItem;

    if (moduleItem.routing && moduleItem.routing.routingGroups && !moduleItem.routing.routingGroups.length) {
      delete moduleItem.routing;
    }

    if (this.routingService.isRoutingDataChanged()) {
      this.routingService.resetIsRoutingDataChanged();
    }

    if (this.isEdit && !this.isDuplicate) {
      moduleItem = {
        ...moduleItem,
        id: this.selectedModuleItem?.id,
      };
      this.store.dispatch(
        updateModuleItem({
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
          moduleItem,
        })
      );
    } else {
      this.store.dispatch(
        createModuleItem({
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
          moduleItem,
        })
      );
    }
  }

  closeModuleItemDialog(): void {
    this.moduleItemDialogRef.close();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
